import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import useScrollBehavior from "../hooks/useScrollBehavior";
import { useMobileDetectContext } from "./MobileDetectContext";

// Define the shape of the context value
interface ScrollVisibilityContextType {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isLogoVisible: boolean;
}

// Create the context with default undefined to enforce provider use
const ScrollVisibilityContext = createContext<
  ScrollVisibilityContextType | undefined
>(undefined);

// Define the provider's prop type
interface ScrollVisibilityProviderProps {
  children: ReactNode;
}

// Create a provider component
export const ScrollVisibilityProvider = ({
  children,
}: ScrollVisibilityProviderProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isLogoVisible, setIsLogoVisible] = useState<boolean>(true);
  const { scrollY, scrollDirection, isNearFooter } = useScrollBehavior();
  const { isMobileOnly } = useMobileDetectContext();
  useEffect(() => {
    if (!isMobileOnly) return;
    if (scrollY == 0 && !isLogoVisible) {
      setIsLogoVisible(true);
    }
    const shouldHide =
      scrollDirection === "down" && scrollY > 1 && !isNearFooter;
    const shouldShow = scrollDirection === "up" || isNearFooter;

    if (shouldHide && isVisible) {
      setIsVisible(false);
      setIsLogoVisible(true);
    } else if (shouldShow && !isVisible) {
      setIsVisible(true);
      setIsLogoVisible(false);
    }

    const handleTouchStart = (event: TouchEvent) => {
      const targetElement = event.target as HTMLElement;
      if (targetElement.tagName === "BODY" || targetElement.tagName === "DIV") {
        if (!isVisible) {
          setIsVisible(true);
          setIsLogoVisible(false);
        }
      }
    };

    document.addEventListener("touchstart", handleTouchStart);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, [
    scrollY,
    scrollDirection,
    isNearFooter,
    isMobileOnly,
    isVisible,
    isLogoVisible,
  ]);

  return (
    <ScrollVisibilityContext.Provider
      value={{ isVisible, setIsVisible, isLogoVisible }}
    >
      {children}
    </ScrollVisibilityContext.Provider>
  );
};

// Custom hook to use the scroll visibility context
export const useScrollVisibility = () => {
  const context = useContext(ScrollVisibilityContext);
  if (!context) {
    throw new Error(
      "useScrollVisibility must be used within a ScrollVisibilityProvider"
    );
  }
  return context;
};
